import type { Post } from "@/types";
import type { RouteLocationRaw } from "vue-router";
import { parseTwitterXStatusUrl } from "@/utils/ogUrlUtils";

/**
 * Canonical author ULID for ownership checks (poll author, post menu, etc.).
 * Aligns with PostCard `postUserId` / `isOwner` resolution across feed shapes.
 */
export function getPostAuthorUlid(
  post: Post | null | undefined,
): string | undefined {
  if (!post) return undefined;
  if (post.userId) return String(post.userId);
  const rec = post as Record<string, unknown>;
  const fromSnake = rec.user_ulid;
  if (typeof fromSnake === "string" && fromSnake) return fromSnake;
  const fromCamel = rec.userULID;
  if (typeof fromCamel === "string" && fromCamel) return fromCamel;
  const user = post.user as { ulid?: string; userId?: string } | undefined;
  if (user?.ulid) return String(user.ulid);
  if (user?.userId) return String(user.userId);
  return undefined;
}

interface TipTapMark {
  type?: string;
  attrs?: Record<string, unknown>;
}

interface TipTapNode {
  type?: string;
  text?: string;
  marks?: TipTapMark[];
  attrs?: Record<string, unknown>;
  content?: TipTapNode[];
}

function normalizePlainTextWhitespace(text: string): string {
  return text
    .replace(/\r\n/g, "\n")
    .replace(/\u00a0/g, " ")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n[ \t]+/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/**
 * Get the route for a post detail page based on post type
 */
export function getPostRoute(post: Post): RouteLocationRaw {
  const ulid = post.ulid || post.id;
  const postType = (post.type || post.postType || "POST").toUpperCase();

  switch (postType) {
    case "SONG":
      return { name: "song-detail", params: { ulid } };
    case "VIDEO":
    case "SHORT":
      return { name: "post-detail", params: { ulid } };
    case "PODCAST":
      return { name: "podcast-detail", params: { ulid } };
    case "BURST":
      return { name: "burst-detail", params: { ulid } };
    default:
      return { name: "post-detail", params: { ulid } };
  }
}

/**
 * Get the URL path for a post based on type. For COMMENT/REPLY entities, returns
 * the root post permalink with `?comment=<commentUlid>` so Copy link, Share, and
 * deep-link navigation all yield the same URL.
 */
export function getPostPath(post: Post): string {
  const ulid = post.ulid || post.id;
  const postType = (post.type || post.postType || "POST").toUpperCase();

  if (postType === "COMMENT" || postType === "REPLY") {
    const rec = post as Record<string, unknown>;
    const rootUlid =
      (typeof rec.postUlid === "string" && rec.postUlid) ||
      (typeof rec.rootPostUlid === "string" && rec.rootPostUlid) ||
      (typeof rec.post_ulid === "string" && rec.post_ulid) ||
      (typeof rec.root_post_ulid === "string" && rec.root_post_ulid) ||
      (typeof rec.rootUlid === "string" && rec.rootUlid) ||
      (typeof rec.parentUlid === "string" && rec.parentUlid) ||
      (typeof rec.parentId === "string" && rec.parentId) ||
      undefined;
    if (rootUlid && ulid) {
      return `/post/${rootUlid}?comment=${ulid}`;
    }
  }

  switch (postType) {
    case "SONG":
      return `/song/${ulid}`;
    case "VIDEO":
    case "SHORT":
      return `/post/${ulid}`;
    case "PODCAST":
      return `/podcast/${ulid}`;
    case "BURST":
      return `/burst/${ulid}`;
    default:
      return `/post/${ulid}`;
  }
}

/**
 * Get the icon for a post type
 */
export function getPostTypeIcon(type: string | undefined): string {
  if (!type) return "mdi-post-outline";
  const iconMap: Record<string, string> = {
    SONG: "mdi-music",
    VIDEO: "mdi-video",
    SHORT: "mdi-video-box",
    PODCAST: "mdi-podcast",
    BURST: "mdi-lightning-bolt",
    POST: "mdi-post-outline",
    REPLY: "mdi-comment",
    REPOST: "mdi-repeat",
    QUOTE: "mdi-format-quote-close",
  };
  return iconMap[type.toUpperCase()] || "mdi-post-outline";
}

/**
 * Get a human-readable label for a post type
 */
export function getPostTypeLabel(type: string | undefined): string {
  if (!type) return "Post";
  const labelMap: Record<string, string> = {
    SONG: "Song",
    VIDEO: "Video",
    SHORT: "Short",
    PODCAST: "Podcast",
    BURST: "Burst",
    POST: "Post",
    REPLY: "Reply",
    REPOST: "Repost",
    QUOTE: "Quote",
  };
  return labelMap[type.toUpperCase()] || "Post";
}

/**
 * Create a song reference string in the format #Slugified-Title[song:ULID]
 * This format is parsed by formatBodyWithLinks to create direct links to songs
 */
export function createSongReference(title: string, ulid: string): string {
  const slugifiedTitle = title
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]/g, '');
  return `#${slugifiedTitle}[song:${ulid}]`;
}

/**
 * Check if content appears to be TipTap JSON ({"type":"doc",...})
 */
export function isTipTapJson(text: string): boolean {
  if (!text.startsWith("{")) return false;
  try {
    const parsed = JSON.parse(text);
    // Accept empty docs (no content array) — they should render as empty, not raw JSON
    return parsed?.type === "doc";
  } catch {
    return false;
  }
}

/**
 * Decode common HTML entities back to their literal characters.
 * The API sometimes stores text with pre-encoded entities (e.g. &#39; for apostrophes).
 * We decode first so escapeHtml doesn't double-encode them (& → &amp; turning &#39; → &amp;#39;).
 */
export function decodeHtmlEntities(text: string): string {
  return text
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&#x27;/g, "'")
    .replace(/&#(\d+);/g, (_match, dec) => String.fromCharCode(Number(dec)));
}

/**
 * Decode HTML entities for display in Vue's interpolated text.
 * Returns the text unchanged if null/undefined/non-string.
 */
export function htmlDecode(text: string | null | undefined): string {
  if (!text) return "";
  return decodeHtmlEntities(text);
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function sanitizeHref(href: unknown): string | null {
  if (typeof href !== "string") return null;

  const trimmed = href.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith("/")) {
    return trimmed;
  }

  try {
    const parsed = new URL(
      trimmed,
      typeof window !== "undefined"
        ? window.location.origin
        : "https://example.invalid",
    );
    if (
      parsed.protocol === "http:" ||
      parsed.protocol === "https:" ||
      parsed.protocol === "mailto:" ||
      parsed.protocol === "tel:"
    ) {
      return trimmed;
    }
  } catch {
    return null;
  }

  return null;
}

/** http(s) and protocol-relative — open in a new tab; same-origin paths stay in-app. */
function isExternalWebHref(href: string): boolean {
  const t = href.trim().toLowerCase();
  return (
    t.startsWith("http://") ||
    t.startsWith("https://") ||
    t.startsWith("//")
  );
}

/** Max visible characters for a bare external URL inside an anchor (includes trailing `...`). */
const EXTERNAL_URL_LINK_DISPLAY_MAX = 48;
const EXTERNAL_URL_TRUNCATION_SUFFIX = "...";

function shouldElideExternalUrlLinkText(href: string, text: string): boolean {
  const h = href.trim();
  const t = text.trim();
  if (!isExternalWebHref(h)) return false;
  const looksUrl = /^https?:\/\//i.test(t) || t.startsWith("//");
  if (!looksUrl) return false;
  if (t === h) return true;
  if (h.startsWith(t) || t.startsWith(h)) return true;
  try {
    const base =
      typeof window !== "undefined" && window.location?.origin
        ? window.location.origin
        : "https://example.invalid";
    return new URL(t, base).href === new URL(h, base).href;
  } catch {
    return false;
  }
}

/**
 * Shorten anchors whose visible text is essentially a long external URL so flex layouts
 * (e.g. single-post text reel) do not grow past the viewport; full URL is kept in `title`.
 */
function shortenExternalUrlAnchorsInHtml(html: string): string {
  if (!html || typeof DOMParser === "undefined") return html;
  try {
    const doc = new DOMParser().parseFromString(
      `<div id="__url_truncate_root">${html}</div>`,
      "text/html",
    );
    const root = doc.getElementById("__url_truncate_root");
    if (!root) return html;
    for (const a of Array.from(root.querySelectorAll("a[href]"))) {
      const href = a.getAttribute("href");
      if (!href) continue;
      if (!isExternalWebHref(href)) continue;
      if (a.getElementsByTagName("*").length > 0) continue;
      const text = (a.textContent ?? "").trim();
      if (!shouldElideExternalUrlLinkText(href, text)) continue;
      if (text.length <= EXTERNAL_URL_LINK_DISPLAY_MAX) continue;
      const keep =
        EXTERNAL_URL_LINK_DISPLAY_MAX - EXTERNAL_URL_TRUNCATION_SUFFIX.length;
      a.textContent = `${text.slice(0, Math.max(0, keep))}${EXTERNAL_URL_TRUNCATION_SUFFIX}`;
      a.setAttribute("title", href);
    }
    return root.innerHTML;
  } catch {
    return html;
  }
}

const ALLOWED_HTML_TAGS = new Set([
  "p",
  "br",
  "strong",
  "em",
  "u",
  "s",
  "a",
  "span",
  "ul",
  "ol",
  "li",
  "blockquote",
  "code",
]);

function sanitizeElementAttribute(
  tagName: string,
  name: string,
  value: string,
): string | null {
  switch (name) {
    case "class":
      if (tagName === "a" || tagName === "span") {
        return escapeHtml(value);
      }
      return null;
    case "href":
      if (tagName !== "a") return null;
      return sanitizeHref(value);
    case "target":
      if (tagName !== "a") return null;
      return value === "_blank" ? "_blank" : null;
    case "rel":
      if (tagName !== "a") return null;
      return "noopener noreferrer";
    case "data-type":
    case "data-id":
    case "data-label":
    case "data-title":
      if (tagName === "span") {
        return escapeHtml(value);
      }
      return null;
    default:
      return null;
  }
}

function sanitizeHtmlNode(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return escapeHtml(node.textContent || "");
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return "";
  }

  const element = node as HTMLElement;
  const tagName = element.tagName.toLowerCase();
  const children = Array.from(element.childNodes)
    .map((child) => sanitizeHtmlNode(child))
    .join("");

  if (!ALLOWED_HTML_TAGS.has(tagName)) {
    return children;
  }

  if (tagName === "br") {
    return "<br>";
  }

  let anchorHrefSanitized: string | null = null;
  const attributes: string[] = [];
  for (const attr of Array.from(element.attributes)) {
    const sanitizedValue = sanitizeElementAttribute(
      tagName,
      attr.name,
      attr.value,
    );
    if (sanitizedValue === null) continue;
    if (tagName === "a" && attr.name === "href") {
      anchorHrefSanitized = sanitizedValue;
    }
    attributes.push(`${attr.name}="${escapeHtml(sanitizedValue)}"`);
  }

  if (tagName === "a" && !attributes.some((attr) => attr.startsWith("href="))) {
    return children;
  }

  if (
    tagName === "a" &&
    anchorHrefSanitized &&
    isExternalWebHref(anchorHrefSanitized)
  ) {
    if (!attributes.some((attr) => attr.startsWith("target="))) {
      attributes.push('target="_blank"');
    }
    if (!attributes.some((attr) => attr.startsWith("rel="))) {
      attributes.push('rel="noopener noreferrer"');
    }
  }

  const attributeString = attributes.length > 0 ? ` ${attributes.join(" ")}` : "";
  return `<${tagName}${attributeString}>${children}</${tagName}>`;
}

function sanitizeRichTextHtml(html: string): string {
  if (typeof DOMParser === "undefined") {
    return escapeHtml(html);
  }

  const parsed = new DOMParser().parseFromString(html, "text/html");
  return Array.from(parsed.body.childNodes)
    .map((child) => sanitizeHtmlNode(child))
    .join("");
}

function htmlNodeToPlainText(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return decodeHtmlEntities(node.textContent || "");
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return "";
  }

  const element = node as HTMLElement;
  const tagName = element.tagName.toLowerCase();
  const children = Array.from(element.childNodes)
    .map((child) => htmlNodeToPlainText(child))
    .join("");

  switch (tagName) {
    case "br":
      return "\n";
    case "li":
      return `• ${children}\n`;
    case "p":
    case "div":
    case "blockquote":
      return `${children}\n\n`;
    case "ul":
    case "ol":
      return `${children}\n`;
    default:
      return children;
  }
}

function htmlToPlainText(html: string): string {
  if (typeof DOMParser === "undefined") {
    return normalizePlainTextWhitespace(
      decodeHtmlEntities(
        html
          .replace(/<br\s*\/?>/gi, "\n")
          .replace(/<\/(p|div|blockquote)>/gi, "\n\n")
          .replace(/<\/li>/gi, "\n")
          .replace(/<[^>]*>/g, ""),
      ),
    );
  }

  const parsed = new DOMParser().parseFromString(html, "text/html");
  return normalizePlainTextWhitespace(
    Array.from(parsed.body.childNodes)
      .map((child) => htmlNodeToPlainText(child))
      .join(""),
  );
}

function wrapWithMarks(text: string, marks: TipTapMark[] = []): string {
  return marks.reduce((html, mark) => {
    switch (mark.type) {
      case "bold":
        return `<strong>${html}</strong>`;
      case "italic":
        return `<em>${html}</em>`;
      case "underline":
        return `<u>${html}</u>`;
      case "strike":
        return `<s>${html}</s>`;
      case "code":
        return `<code>${html}</code>`;
      case "link": {
        const href = sanitizeHref(mark.attrs?.href);
        if (!href) return html;
        const blank = isExternalWebHref(href) ? ' target="_blank"' : "";
        return `<a href="${escapeHtml(href)}"${blank} rel="noopener noreferrer">${html}</a>`;
      }
      default:
        return html;
    }
  }, text);
}

function renderTipTapNode(
  node: TipTapNode,
  mentionClass: string,
  hashtagClass: string,
): string {
  const children = (node.content || [])
    .map((child) => renderTipTapNode(child, mentionClass, hashtagClass))
    .join("");

  switch (node.type) {
    case "doc":
      return children;
    case "paragraph":
      return `<p>${children || "<br>"}</p>`;
    case "text":
      return wrapWithMarks(
        formatHashtagsAndMentions(
          escapeHtml(node.text || ""),
          mentionClass,
          hashtagClass,
        ),
        node.marks,
      );
    case "hardBreak":
      return "<br>";
    case "bulletList":
      return `<ul>${children}</ul>`;
    case "orderedList":
      return `<ol>${children}</ol>`;
    case "listItem":
      return `<li>${children}</li>`;
    case "blockquote":
      return `<blockquote>${children}</blockquote>`;
    case "mention": {
      const label =
        typeof node.attrs?.label === "string" ? node.attrs.label : "";
      return `<a href="/profile/${encodeURIComponent(label)}" class="${mentionClass}" onclick="event.stopPropagation()">@${escapeHtml(label)}</a>`;
    }
    case "songReference": {
      const id = typeof node.attrs?.id === "string" ? node.attrs.id : "";
      const title =
        typeof node.attrs?.title === "string" ? node.attrs.title : "";
      const slugifiedTitle = title
        .replace(/\s+/g, "-")
        .replace(/[^\w-]/g, "");
      return `<a href="/song/${escapeHtml(id)}" class="${hashtagClass}" onclick="event.stopPropagation()">#${escapeHtml(slugifiedTitle)}</a>`;
    }
    default:
      return children;
  }
}

/**
 * Convert TipTap JSON string to HTML using a minimal renderer for feed/detail display.
 */
function tiptapJsonToHtml(
  text: string,
  mentionClass: string,
  hashtagClass: string,
): string {
  try {
    const json = JSON.parse(text) as TipTapNode;
    return renderTipTapNode(json, mentionClass, hashtagClass);
  } catch {
    return text;
  }
}

/**
 * Check if content appears to be HTML (from rich text editor)
 */
function isHtmlContent(text: string): boolean {
  // Check for common TipTap/HTML tags
  return /<(p|div|span|strong|em|u|s|a|ul|ol|li|blockquote|br)\b[^>]*>/i.test(text);
}

/**
 * Convert TipTap mention spans to clickable links
 * Input: <span data-type="mention" class="mention" data-id="ULID" data-label="username">@username</span>
 * Output: <a href="/profile/username" class="mention" onclick="event.stopPropagation()">@username</a>
 */
function convertMentionSpansToLinks(html: string): string {
  return html.replace(
    /<span[^>]*data-type="mention"[^>]*data-label="([^"]*)"[^>]*>([^<]*)<\/span>/gi,
    '<a href="/profile/$1" class="mention" onclick="event.stopPropagation()">$2</a>'
  );
}

/**
 * Convert TipTap song reference spans to clickable links
 * Input: <span data-type="songReference" class="song-reference" data-id="ULID" data-title="Title">#Title</span>
 * Output: <a href="/song/ULID" class="song-reference" onclick="event.stopPropagation()">#Title</a>
 */
function convertSongReferenceSpansToLinks(html: string): string {
  return html.replace(
    /<span[^>]*data-type="songReference"[^>]*data-id="([^"]*)"[^>]*>([^<]*)<\/span>/gi,
    '<a href="/song/$1" class="song-reference" onclick="event.stopPropagation()">$2</a>'
  );
}

/**
 * Format post body text with clickable @mentions and #hashtags
 * For plain text: escapes HTML to prevent XSS and converts mentions/hashtags to links
 * For HTML content (from rich text editor): sanitizes with DOMPurify and converts TipTap nodes to links
 * - @username -> link to /profile/username
 * - #hashtag[song:ULID] -> link to /song/ULID (song reference)
 * - #hashtag -> link to /search?q=%23hashtag (regular hashtag)
 */
/** Normalize literal \\n (from cross-posting etc.) to real newlines so they can be rendered as line breaks. */
function normalizeEscapedNewlines(text: string): string {
  return text
    .replace(/\\r\\n/g, "\n")
    .replace(/\\n/g, "\n")
    .replace(/\\r/g, "\n");
}

/** Convert newlines to <br> for HTML display (v-html). */
function newlinesToBr(html: string): string {
  return html.replace(/\n/g, "<br>");
}

export function formatBodyWithLinks(
  text: string,
  mentionClass = "mention",
  hashtagClass = "hashtag"
): string {
  if (!text) return "";
  let shouldLinkifyHtmlContent = true;

  // Check for TipTap JSON BEFORE normalizing newlines — normalizeEscapedNewlines
  // corrupts JSON by replacing literal \n inside the string, breaking JSON.parse
  if (isTipTapJson(text)) {
    text = tiptapJsonToHtml(text, mentionClass, hashtagClass);
    shouldLinkifyHtmlContent = false;
  } else {
    // Only normalize escaped newlines for non-JSON content (plain text, cross-posted text)
    text = normalizeEscapedNewlines(text);
  }

  // If content is HTML (from rich text editor or converted from TipTap JSON), sanitize and convert nodes to links
  if (isHtmlContent(text)) {
    let sanitized = sanitizeRichTextHtml(text);

    if (shouldLinkifyHtmlContent) {
      sanitized = formatHashtagsAndMentions(sanitized, mentionClass, hashtagClass);
    }

    // Convert TipTap mention/song-reference spans to clickable links
    sanitized = convertMentionSpansToLinks(sanitized);
    sanitized = convertSongReferenceSpansToLinks(sanitized);
    sanitized = shortenExternalUrlAnchorsInHtml(sanitized);

    return newlinesToBr(sanitized);
  }

  // Decode pre-encoded HTML entities first (so #39 in &#39; isn't linkified as a
  // hashtag), then escapeHtml the result before any link/markdown transforms.
  // Without the escape, decoded user text containing <script>/<img onerror>/<iframe>
  // would reach v-html unsanitized.
  let formatted = escapeHtml(decodeHtmlEntities(text));

  // Convert basic markdown formatting to HTML (after escaping, so it's safe)
  // Bold: **text** → <strong>text</strong>
  formatted = formatted.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  // Italic: *text* → <em>text</em> (but not inside <strong> tags)
  formatted = formatted.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, "<em>$1</em>");

  formatted = formatHashtagsAndMentions(formatted, mentionClass, hashtagClass);

  return newlinesToBr(formatted);
}

export function formatBodyAsPlainText(text: string): string {
  if (!text) return "";
  return htmlToPlainText(
    formatBodyWithLinks(text, "__plain-text-mention", "__plain-text-hashtag"),
  );
}

function formatHashtagsAndMentions(text: string, mentionClass: string, hashtagClass: string): string {
  let formatted = text

  // Replace @mentions with links
  formatted = formatted.replace(
    /@([a-zA-Z0-9_]+)/g,
    `<a href="/profile/$1" class="${mentionClass}" onclick="event.stopPropagation()">@$1</a>`
  );

  // Replace #hashtag[song:ULID] with links to song pages
  formatted = formatted.replace(
    /#([\w-]+)\[song:([a-zA-Z0-9]+)\]/g,
    `<a href="/song/$2" class="${hashtagClass}" onclick="event.stopPropagation()">#$1</a>`
  );

  // Replace regular #hashtags with search links (including hyphens which are common in hashtags)
  // Skip:
  //  - hashtags already inside anchor tags from the song-reference replacement (?<!>)
  //  - numeric character references like &#39; that survive escapeHtml; wrapping the
  //    `#39` in an anchor would break the entity, so `Mariah&#39;s` renders as
  //    literal `Mariah&` + linked `#39` + `;s` instead of `Mariah's`
  formatted = formatted.replace(
    /(?<![>&])#(?!\d+;)([\w-]+)/g,
    `<a href="/search?q=%23$1" class="${hashtagClass}" onclick="event.stopPropagation()">#$1</a>`
  );

  return formatted;
}

/**
 * YouTube video info extracted from a URL
 */
export interface YouTubeVideoInfo {
  videoId: string;
  url: string;
  thumbnailUrl: string;
  embedUrl: string;
}

/**
 * Extract YouTube video ID from various URL formats
 * Supports: youtube.com/watch?v=, youtu.be/, youtube.com/embed/, youtube.com/shorts/
 */
export function extractYouTubeVideoId(url: string): string | null {
  if (!url) return null;

  // Standard youtube.com/watch?v= format
  const watchMatch = url.match(/(?:youtube\.com\/watch\?v=|youtube\.com\/watch\?.*&v=)([a-zA-Z0-9_-]{11})/);
  if (watchMatch) return watchMatch[1];

  // Short youtu.be/ format
  const shortMatch = url.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/);
  if (shortMatch) return shortMatch[1];

  // Embed format youtube.com/embed/
  const embedMatch = url.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/);
  if (embedMatch) return embedMatch[1];

  // Shorts format youtube.com/shorts/
  const shortsMatch = url.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/);
  if (shortsMatch) return shortsMatch[1];

  return null;
}

/**
 * Extract YouTube video info from text content
 * Returns the first YouTube link found in the text
 */
export function extractYouTubeFromText(text: string): YouTubeVideoInfo | null {
  if (!text) return null;

  // Match YouTube URLs in text
  const urlRegex = /https?:\/\/(?:www\.)?(?:youtube\.com\/(?:watch\?[^\s]*v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})[^\s]*/gi;
  const match = urlRegex.exec(text);

  if (match) {
    const videoId = extractYouTubeVideoId(match[0]);
    if (videoId) {
      return {
        videoId,
        url: match[0],
        // Use maxresdefault with fallback to hqdefault (some videos don't have maxres)
        thumbnailUrl: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
        embedUrl: `https://www.youtube.com/embed/${videoId}`,
      };
    }
  }

  return null;
}

/**
 * Get the canonical YouTube watch URL for a video
 */
export function getYouTubeWatchUrl(videoId: string): string {
  return `https://www.youtube.com/watch?v=${videoId}`;
}

/**
 * Remove YouTube URLs from text content
 * Used to hide the raw URL when displaying a YouTube preview
 */
export function stripYouTubeUrls(text: string): string {
  if (!text) return "";

  // Remove YouTube URLs (supports all formats including youtu.be short links)
  return text
    .replace(/https?:\/\/(?:www\.)?(?:youtube\.com\/(?:watch\?[^\s]*|embed\/[^\s]*|shorts\/[^\s]*)|youtu\.be\/[^\s]*)/gi, "")
    .replace(/\s+/g, " ") // Collapse multiple spaces
    .trim();
}

// ============================================================
// SPOTIFY EMBED UTILITIES
// ============================================================

export type SpotifyContentType = 'track' | 'album' | 'playlist' | 'artist' | 'episode' | 'show';

export interface SpotifyEmbedInfo {
  type: SpotifyContentType;
  id: string;
  url: string;
  embedUrl: string;
}

/**
 * Extract Spotify content info from URL
 * Supports: open.spotify.com/track|album|playlist|artist|episode|show/ID
 */
export function extractSpotifyFromText(text: string): SpotifyEmbedInfo | null {
  if (!text) return null;

  // Match Spotify URLs - open.spotify.com/{type}/{id}
  const regex = /https?:\/\/open\.spotify\.com\/(track|album|playlist|artist|episode|show)\/([a-zA-Z0-9]+)[^\s]*/gi;
  const match = regex.exec(text);

  if (match) {
    const type = match[1].toLowerCase() as SpotifyContentType;
    const id = match[2];
    return {
      type,
      id,
      url: match[0],
      embedUrl: `https://open.spotify.com/embed/${type}/${id}?utm_source=generator&theme=0`,
    };
  }

  return null;
}

/**
 * Remove Spotify URLs from text
 */
export function stripSpotifyUrls(text: string): string {
  if (!text) return "";
  return text
    .replace(/https?:\/\/open\.spotify\.com\/[^\s]+/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

// ============================================================
// SOUNDCLOUD EMBED UTILITIES
// ============================================================

export interface SoundCloudEmbedInfo {
  url: string;
  embedUrl: string;
}

/**
 * Extract SoundCloud info from URL
 * SoundCloud requires oEmbed API call to get embed HTML, but we can detect the URL
 * Note: Only full URLs (soundcloud.com) work with the widget embed.
 * Short URLs (on.soundcloud.com) don't work with the widget - they need to be resolved first.
 */
export function extractSoundCloudFromText(text: string): SoundCloudEmbedInfo | null {
  if (!text) return null;

  // Match full SoundCloud URLs (soundcloud.com/artist/track) - these work with the widget
  const fullRegex = /https?:\/\/(?:www\.)?soundcloud\.com\/[a-zA-Z0-9_-]+\/[a-zA-Z0-9_-]+[^\s]*/gi;
  const match = fullRegex.exec(text);

  if (match) {
    const url = match[0];
    // SoundCloud widget URL with encoded track URL
    const params = `color=%23ff5500&auto_play=false&hide_related=true`
      + `&show_comments=false&show_user=true&show_reposts=false&show_teaser=false&visual=true`;
    const embedUrl = `https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}&${params}`;
    return { url, embedUrl };
  }

  // Short URLs (on.soundcloud.com) don't work with widget, so we don't extract them for embedding
  // They will just appear as regular links in the post text

  return null;
}

/**
 * Remove SoundCloud URLs from text (both full and short URLs)
 */
export function stripSoundCloudUrls(text: string): string {
  if (!text) return "";
  return text
    .replace(/https?:\/\/(?:www\.)?soundcloud\.com\/[^\s]+/gi, "")
    .replace(/https?:\/\/on\.soundcloud\.com\/[^\s]+/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

// ============================================================
// TWITTER/X EMBED UTILITIES
// ============================================================

export interface TwitterEmbedInfo {
  tweetId: string;
  username: string;
  url: string;
}

/**
 * Extract Twitter/X tweet info from URL
 * Supports: twitter.com and x.com
 */
export function extractTwitterFromText(text: string): TwitterEmbedInfo | null {
  if (!text) return null;

  const inText =
    /https?:\/\/(?:www\.|m\.|mobile\.)?(?:twitter\.com|x\.com)\/[^\s]+/gi;
  let m: RegExpExecArray | null;
  while ((m = inText.exec(text)) !== null) {
    const candidate = m[0];
    const st = parseTwitterXStatusUrl(candidate);
    if (st) {
      return {
        username: st.username,
        tweetId: st.tweetId,
        url: candidate,
      };
    }
  }

  return null;
}

/**
 * Remove Twitter/X URLs from text
 */
export function stripTwitterUrls(text: string): string {
  if (!text) return "";
  return text
    .replace(/https?:\/\/(?:www\.)?(?:twitter\.com|x\.com)\/[^\s]+/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

// ============================================================
// TIKTOK EMBED UTILITIES
// ============================================================

export interface TikTokEmbedInfo {
  videoId: string;
  username: string;
  url: string;
}

/**
 * Extract TikTok video info from URL
 * Supports: tiktok.com/@user/video/ID and vm.tiktok.com short links
 */
export function extractTikTokFromText(text: string): TikTokEmbedInfo | null {
  if (!text) return null;

  // Match TikTok URLs - tiktok.com/@username/video/videoId
  const regex = /https?:\/\/(?:www\.)?tiktok\.com\/@([a-zA-Z0-9_.]+)\/video\/(\d+)[^\s]*/gi;
  const match = regex.exec(text);

  if (match) {
    return {
      username: match[1],
      videoId: match[2],
      url: match[0],
    };
  }

  // Also check for vm.tiktok.com short links (we'll just detect them, actual ID requires redirect)
  const shortRegex = /https?:\/\/vm\.tiktok\.com\/[a-zA-Z0-9]+[^\s]*/gi;
  const shortMatch = shortRegex.exec(text);

  if (shortMatch) {
    return {
      username: '',
      videoId: '',
      url: shortMatch[0],
    };
  }

  return null;
}

/**
 * Remove TikTok URLs from text
 */
export function stripTikTokUrls(text: string): string {
  if (!text) return "";
  return text
    .replace(/https?:\/\/(?:www\.)?tiktok\.com\/[^\s]+/gi, "")
    .replace(/https?:\/\/vm\.tiktok\.com\/[^\s]+/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

// ============================================================
// VIMEO EMBED UTILITIES (Medium Priority)
// ============================================================

export interface VimeoEmbedInfo {
  videoId: string;
  url: string;
  embedUrl: string;
}

/**
 * Extract Vimeo video info from URL
 * Supports: vimeo.com/{id} and player.vimeo.com/video/{id}
 */
export function extractVimeoFromText(text: string): VimeoEmbedInfo | null {
  if (!text) return null;

  const regex = /https?:\/\/(?:www\.)?(?:vimeo\.com\/|player\.vimeo\.com\/video\/)(\d+)[^\s]*/gi;
  const match = regex.exec(text);

  if (match) {
    const videoId = match[1];
    return {
      videoId,
      url: match[0],
      embedUrl: `https://player.vimeo.com/video/${videoId}?autoplay=0&title=0&byline=0&portrait=0`,
    };
  }

  return null;
}

/**
 * Remove Vimeo URLs from text
 */
export function stripVimeoUrls(text: string): string {
  if (!text) return "";
  return text
    .replace(/https?:\/\/(?:www\.)?(?:vimeo\.com\/|player\.vimeo\.com\/video\/)[^\s]+/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

// ============================================================
// TWITCH EMBED UTILITIES (Medium Priority)
// ============================================================

export interface TwitchEmbedInfo {
  type: 'channel' | 'video' | 'clip';
  id: string;
  url: string;
}

/**
 * Extract Twitch info from URL
 * Supports: twitch.tv/{channel}, twitch.tv/videos/{id}, clips.twitch.tv/{slug}
 */
export function extractTwitchFromText(text: string): TwitchEmbedInfo | null {
  if (!text) return null;

  // Video URL
  const videoRegex = /https?:\/\/(?:www\.)?twitch\.tv\/videos\/(\d+)[^\s]*/gi;
  const videoMatch = videoRegex.exec(text);
  if (videoMatch) {
    return { type: 'video', id: videoMatch[1], url: videoMatch[0] };
  }

  // Clip URL (clips.twitch.tv)
  const clipRegex = /https?:\/\/clips\.twitch\.tv\/([a-zA-Z0-9_-]+)[^\s]*/gi;
  const clipMatch = clipRegex.exec(text);
  if (clipMatch) {
    return { type: 'clip', id: clipMatch[1], url: clipMatch[0] };
  }

  // Clip URL (twitch.tv/{channel}/clip/{slug})
  const clipRegex2 = /https?:\/\/(?:www\.)?twitch\.tv\/[a-zA-Z0-9_]+\/clip\/([a-zA-Z0-9_-]+)[^\s]*/gi;
  const clipMatch2 = clipRegex2.exec(text);
  if (clipMatch2) {
    return { type: 'clip', id: clipMatch2[1], url: clipMatch2[0] };
  }

  // Channel URL (must be last to avoid matching video/clip URLs)
  const channelRegex = /https?:\/\/(?:www\.)?twitch\.tv\/([a-zA-Z0-9_]+)(?:\?[^\s]*)?(?:$|\s)/gi;
  const channelMatch = channelRegex.exec(text);
  if (channelMatch) {
    return { type: 'channel', id: channelMatch[1], url: channelMatch[0].trim() };
  }

  return null;
}

/**
 * Remove Twitch URLs from text
 */
export function stripTwitchUrls(text: string): string {
  if (!text) return "";
  return text
    .replace(/https?:\/\/(?:www\.)?twitch\.tv\/[^\s]+/gi, "")
    .replace(/https?:\/\/clips\.twitch\.tv\/[^\s]+/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

// ============================================================
// APPLE MUSIC EMBED UTILITIES (Medium Priority)
// ============================================================

export type AppleMusicContentType = 'album' | 'playlist' | 'song' | 'music-video' | 'artist';

export interface AppleMusicEmbedInfo {
  type: AppleMusicContentType;
  country: string;
  id: string;
  url: string;
  embedUrl: string;
}

/**
 * Extract Apple Music info from URL
 * Supports: music.apple.com/{country}/{type}/{name}/{id}
 */
export function extractAppleMusicFromText(text: string): AppleMusicEmbedInfo | null {
  if (!text) return null;

  const regex = /https?:\/\/music\.apple\.com\/([a-z]{2})\/(album|playlist|song|music-video|artist)\/[^\/]+\/([a-zA-Z0-9.]+)[^\s]*/gi;
  const match = regex.exec(text);

  if (match) {
    const country = match[1];
    const type = match[2] as AppleMusicContentType;
    const id = match[3];
    return {
      type,
      country,
      id,
      url: match[0],
      embedUrl: `https://embed.music.apple.com/${country}/${type}/${id}`,
    };
  }

  return null;
}

/**
 * Remove Apple Music URLs from text
 */
export function stripAppleMusicUrls(text: string): string {
  if (!text) return "";
  return text
    .replace(/https?:\/\/music\.apple\.com\/[^\s]+/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

// ============================================================
// BANDCAMP EMBED UTILITIES (Medium Priority)
// ============================================================

export interface BandcampEmbedInfo {
  type: 'track' | 'album';
  artist: string;
  slug: string;
  url: string;
}

/**
 * Extract Bandcamp info from URL
 * Supports: {artist}.bandcamp.com/track/{slug} and {artist}.bandcamp.com/album/{slug}
 */
export function extractBandcampFromText(text: string): BandcampEmbedInfo | null {
  if (!text) return null;

  const regex = /https?:\/\/([a-zA-Z0-9_-]+)\.bandcamp\.com\/(track|album)\/([a-zA-Z0-9_-]+)[^\s]*/gi;
  const match = regex.exec(text);

  if (match) {
    return {
      type: match[2] as 'track' | 'album',
      artist: match[1],
      slug: match[3],
      url: match[0],
    };
  }

  return null;
}

/**
 * Remove Bandcamp URLs from text
 */
export function stripBandcampUrls(text: string): string {
  if (!text) return "";
  return text
    .replace(/https?:\/\/[a-zA-Z0-9_-]+\.bandcamp\.com\/[^\s]+/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

// ============================================================
// FACEBOOK EMBED UTILITIES (Low Priority)
// ============================================================

export interface FacebookEmbedInfo {
  type: 'post' | 'video' | 'watch';
  url: string;
}

/**
 * Extract Facebook post/video info from URL
 */
export function extractFacebookFromText(text: string): FacebookEmbedInfo | null {
  if (!text) return null;

  // Facebook Watch video
  const watchRegex = /https?:\/\/(?:www\.)?facebook\.com\/watch\/?\?v=(\d+)[^\s]*/gi;
  const watchMatch = watchRegex.exec(text);
  if (watchMatch) {
    return { type: 'watch', url: watchMatch[0] };
  }

  // Facebook video
  const videoRegex = /https?:\/\/(?:www\.)?facebook\.com\/[a-zA-Z0-9.]+\/videos\/[^\s]+/gi;
  const videoMatch = videoRegex.exec(text);
  if (videoMatch) {
    return { type: 'video', url: videoMatch[0] };
  }

  // Facebook post
  const postRegex = /https?:\/\/(?:www\.)?facebook\.com\/[a-zA-Z0-9.]+\/posts\/[^\s]+/gi;
  const postMatch = postRegex.exec(text);
  if (postMatch) {
    return { type: 'post', url: postMatch[0] };
  }

  return null;
}

/**
 * Remove Facebook URLs from text
 */
export function stripFacebookUrls(text: string): string {
  if (!text) return "";
  return text
    .replace(/https?:\/\/(?:www\.)?facebook\.com\/[^\s]+/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

// ============================================================
// LINKEDIN EMBED UTILITIES (Low Priority)
// ============================================================

export interface LinkedInEmbedInfo {
  type: 'post' | 'article';
  url: string;
}

/**
 * Extract LinkedIn post/article info from URL
 */
export function extractLinkedInFromText(text: string): LinkedInEmbedInfo | null {
  if (!text) return null;

  // LinkedIn article
  const articleRegex = /https?:\/\/(?:www\.)?linkedin\.com\/pulse\/[^\s]+/gi;
  const articleMatch = articleRegex.exec(text);
  if (articleMatch) {
    return { type: 'article', url: articleMatch[0] };
  }

  // LinkedIn post
  const postRegex = /https?:\/\/(?:www\.)?linkedin\.com\/(?:feed\/update\/|posts\/)[^\s]+/gi;
  const postMatch = postRegex.exec(text);
  if (postMatch) {
    return { type: 'post', url: postMatch[0] };
  }

  return null;
}

/**
 * Remove LinkedIn URLs from text
 */
export function stripLinkedInUrls(text: string): string {
  if (!text) return "";
  return text
    .replace(/https?:\/\/(?:www\.)?linkedin\.com\/[^\s]+/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

// ============================================================
// PINTEREST EMBED UTILITIES (Low Priority)
// ============================================================

export interface PinterestEmbedInfo {
  type: 'pin' | 'board';
  id: string;
  url: string;
}

/**
 * Extract Pinterest pin/board info from URL
 */
export function extractPinterestFromText(text: string): PinterestEmbedInfo | null {
  if (!text) return null;

  // Pinterest pin
  const pinRegex = /https?:\/\/(?:www\.)?pinterest\.com\/pin\/(\d+)[^\s]*/gi;
  const pinMatch = pinRegex.exec(text);
  if (pinMatch) {
    return { type: 'pin', id: pinMatch[1], url: pinMatch[0] };
  }

  // Pinterest board
  const boardRegex = /https?:\/\/(?:www\.)?pinterest\.com\/([a-zA-Z0-9_]+)\/([a-zA-Z0-9_-]+)[^\s]*/gi;
  const boardMatch = boardRegex.exec(text);
  if (boardMatch) {
    return { type: 'board', id: `${boardMatch[1]}/${boardMatch[2]}`, url: boardMatch[0] };
  }

  return null;
}

/**
 * Remove Pinterest URLs from text
 */
export function stripPinterestUrls(text: string): string {
  if (!text) return "";
  return text
    .replace(/https?:\/\/(?:www\.)?pinterest\.com\/[^\s]+/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

// ============================================================
// REDDIT EMBED UTILITIES (Low Priority)
// ============================================================

export interface RedditEmbedInfo {
  subreddit: string;
  postId: string;
  url: string;
}

/**
 * Extract Reddit post info from URL
 */
export function extractRedditFromText(text: string): RedditEmbedInfo | null {
  if (!text) return null;

  // Reddit post URL
  const regex = /https?:\/\/(?:www\.)?reddit\.com\/r\/([a-zA-Z0-9_]+)\/comments\/([a-zA-Z0-9]+)[^\s]*/gi;
  const match = regex.exec(text);

  if (match) {
    return {
      subreddit: match[1],
      postId: match[2],
      url: match[0],
    };
  }

  return null;
}

/**
 * Remove Reddit URLs from text
 */
export function stripRedditUrls(text: string): string {
  if (!text) return "";
  return text
    .replace(/https?:\/\/(?:www\.)?reddit\.com\/[^\s]+/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

// ============================================================
// MIXCLOUD EMBED UTILITIES (Low Priority)
// ============================================================

export interface MixcloudEmbedInfo {
  username: string;
  slug: string;
  url: string;
  embedUrl: string;
}

/**
 * Extract Mixcloud info from URL
 * Supports: mixcloud.com/{username}/{slug}
 */
export function extractMixcloudFromText(text: string): MixcloudEmbedInfo | null {
  if (!text) return null;

  const regex = /https?:\/\/(?:www\.)?mixcloud\.com\/([a-zA-Z0-9_-]+)\/([a-zA-Z0-9_-]+)[^\s]*/gi;
  const match = regex.exec(text);

  if (match) {
    const username = match[1];
    const slug = match[2];
    return {
      username,
      slug,
      url: match[0],
      embedUrl: `https://www.mixcloud.com/widget/iframe/?feed=%2F${username}%2F${slug}%2F&hide_cover=1`,
    };
  }

  return null;
}

/**
 * Remove Mixcloud URLs from text
 */
export function stripMixcloudUrls(text: string): string {
  if (!text) return "";
  return text
    .replace(/https?:\/\/(?:www\.)?mixcloud\.com\/[^\s]+/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

// ============================================================
// DAILYMOTION EMBED UTILITIES (Low Priority)
// ============================================================

export interface DailymotionEmbedInfo {
  videoId: string;
  url: string;
  embedUrl: string;
}

/**
 * Extract Dailymotion video info from URL
 * Supports: dailymotion.com/video/{id} and dai.ly/{id}
 */
export function extractDailymotionFromText(text: string): DailymotionEmbedInfo | null {
  if (!text) return null;

  // Full URL format
  const fullRegex = /https?:\/\/(?:www\.)?dailymotion\.com\/video\/([a-zA-Z0-9]+)[^\s]*/gi;
  const fullMatch = fullRegex.exec(text);
  if (fullMatch) {
    const videoId = fullMatch[1];
    return {
      videoId,
      url: fullMatch[0],
      embedUrl: `https://www.dailymotion.com/embed/video/${videoId}`,
    };
  }

  // Short URL format
  const shortRegex = /https?:\/\/dai\.ly\/([a-zA-Z0-9]+)[^\s]*/gi;
  const shortMatch = shortRegex.exec(text);
  if (shortMatch) {
    const videoId = shortMatch[1];
    return {
      videoId,
      url: shortMatch[0],
      embedUrl: `https://www.dailymotion.com/embed/video/${videoId}`,
    };
  }

  return null;
}

/**
 * Remove Dailymotion URLs from text
 */
export function stripDailymotionUrls(text: string): string {
  if (!text) return "";
  return text
    .replace(/https?:\/\/(?:www\.)?dailymotion\.com\/[^\s]+/gi, "")
    .replace(/https?:\/\/dai\.ly\/[^\s]+/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

// ============================================================
// UNIFIED EMBED DETECTION
// ============================================================

export type EmbedType =
  | 'youtube' | 'spotify' | 'soundcloud' | 'twitter' | 'tiktok'
  | 'vimeo' | 'twitch' | 'applemusic' | 'bandcamp'
  | 'facebook' | 'linkedin' | 'pinterest' | 'reddit' | 'mixcloud' | 'dailymotion';

export type EmbedData =
  | YouTubeVideoInfo | SpotifyEmbedInfo | SoundCloudEmbedInfo | TwitterEmbedInfo | TikTokEmbedInfo
  | VimeoEmbedInfo | TwitchEmbedInfo | AppleMusicEmbedInfo | BandcampEmbedInfo
  | FacebookEmbedInfo | LinkedInEmbedInfo | PinterestEmbedInfo | RedditEmbedInfo | MixcloudEmbedInfo | DailymotionEmbedInfo;

export interface EmbedInfo {
  type: EmbedType;
  data: EmbedData;
}

/**
 * Detect and extract the first embeddable content from text
 * Returns the first match found in priority order
 */
export function extractEmbedFromText(text: string): EmbedInfo | null {
  if (!text) return null;

  // High priority - video/music platforms
  const youtube = extractYouTubeFromText(text);
  if (youtube) return { type: 'youtube', data: youtube };

  const spotify = extractSpotifyFromText(text);
  if (spotify) return { type: 'spotify', data: spotify };

  const soundcloud = extractSoundCloudFromText(text);
  if (soundcloud) return { type: 'soundcloud', data: soundcloud };

  const twitter = extractTwitterFromText(text);
  if (twitter) return { type: 'twitter', data: twitter };

  const tiktok = extractTikTokFromText(text);
  if (tiktok) return { type: 'tiktok', data: tiktok };

  // Medium priority - alternative video/music platforms
  const vimeo = extractVimeoFromText(text);
  if (vimeo) return { type: 'vimeo', data: vimeo };

  const twitch = extractTwitchFromText(text);
  if (twitch) return { type: 'twitch', data: twitch };

  const applemusic = extractAppleMusicFromText(text);
  if (applemusic) return { type: 'applemusic', data: applemusic };

  const bandcamp = extractBandcampFromText(text);
  if (bandcamp) return { type: 'bandcamp', data: bandcamp };

  // Low priority - other social platforms
  const facebook = extractFacebookFromText(text);
  if (facebook) return { type: 'facebook', data: facebook };

  const linkedin = extractLinkedInFromText(text);
  if (linkedin) return { type: 'linkedin', data: linkedin };

  const pinterest = extractPinterestFromText(text);
  if (pinterest) return { type: 'pinterest', data: pinterest };

  const reddit = extractRedditFromText(text);
  if (reddit) return { type: 'reddit', data: reddit };

  const mixcloud = extractMixcloudFromText(text);
  if (mixcloud) return { type: 'mixcloud', data: mixcloud };

  const dailymotion = extractDailymotionFromText(text);
  if (dailymotion) return { type: 'dailymotion', data: dailymotion };

  return null;
}

/**
 * Strip all embed URLs from text based on detected embed type
 */
export function stripEmbedUrls(text: string, embedType: EmbedType): string {
  switch (embedType) {
    // High priority
    case 'youtube':
      return stripYouTubeUrls(text);
    case 'spotify':
      return stripSpotifyUrls(text);
    case 'soundcloud':
      return stripSoundCloudUrls(text);
    case 'twitter':
      return stripTwitterUrls(text);
    case 'tiktok':
      return stripTikTokUrls(text);
    // Medium priority
    case 'vimeo':
      return stripVimeoUrls(text);
    case 'twitch':
      return stripTwitchUrls(text);
    case 'applemusic':
      return stripAppleMusicUrls(text);
    case 'bandcamp':
      return stripBandcampUrls(text);
    // Low priority
    case 'facebook':
      return stripFacebookUrls(text);
    case 'linkedin':
      return stripLinkedInUrls(text);
    case 'pinterest':
      return stripPinterestUrls(text);
    case 'reddit':
      return stripRedditUrls(text);
    case 'mixcloud':
      return stripMixcloudUrls(text);
    case 'dailymotion':
      return stripDailymotionUrls(text);
    default:
      return text;
  }
}
